import configs from '@/config/index';
import { formatRupiahAmount } from '@/utils/money';

export const PRODUCT_SEARCH_HISTORY_KEY = 'product_search_history';

const toTrimmedString = value => {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).trim();
};

const toFiniteNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatCompactNumber = value => {
  const number = Math.max(0, Math.floor(toFiniteNumber(value)));
  if (number < 1000) {
    return String(number);
  }

  const compact = number / 1000;
  return `${compact % 1 === 0 ? compact.toFixed(0) : compact.toFixed(1)}k`;
};

export function normalizeRentalSchemes(schemes = []) {
  if (!Array.isArray(schemes)) {
    return [];
  }

  const seen = new Set();
  return schemes.reduce((result, item) => {
    if (!item || typeof item !== 'object') {
      return result;
    }

    const code = toTrimmedString(item.code);
    const name = toTrimmedString(item.name || code);
    const color = toTrimmedString(item.color);
    const identity = code || name;
    if (!identity || seen.has(identity)) {
      return result;
    }

    seen.add(identity);
    result.push({ code, name, color });
    return result;
  }, []);
}

/**
 * 与 Android AppConfig.resolveUrlOrNull 保持一致：完整地址原样返回，
 * 协议相对地址补 https，其余相对路径拼接当前环境的资源域名。
 */
export function resolveAssetUrl(rawUrl = '') {
  const value = toTrimmedString(rawUrl);
  if (!value) {
    return '';
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  if (value.startsWith('//')) {
    return `https:${value}`;
  }
  if (/^(?:data|blob):/i.test(value)) {
    return value;
  }

  const runtimeOrigin = typeof window !== 'undefined' && window.location
    ? toTrimmedString(window.location.origin)
    : '';
  const baseUrl = toTrimmedString(configs.apiUrl) || runtimeOrigin;
  if (!baseUrl || baseUrl === 'null') {
    return value;
  }
  return `${baseUrl.replace(/\/+$/, '')}/${value.replace(/^\/+/, '')}`;
}

/**
 * category/list 与 goods/list 的商品记录统一为页面稳定模型。
 * 后端字段真实拼写是 minPriceSukInfo，只在这里转换为 minPriceSkuInfo。
 */
export function normalizeProduct(rawProduct = {}) {
  const raw = rawProduct && typeof rawProduct === 'object' ? rawProduct : {};
  const rawSkuInfo = raw.minPriceSukInfo || raw.minPriceSkuInfo;
  const goodsId = toTrimmedString(raw.goodsId || raw.id);
  const salesVolume = Math.max(0, Math.floor(toFiniteNumber(raw.salesVolume)));
  const score = Math.max(0, Math.min(5, toFiniteNumber(raw.score)));
  const skuInfo = rawSkuInfo && typeof rawSkuInfo === 'object'
    ? {
        price: toFiniteNumber(rawSkuInfo.price),
        rentalCycle: toTrimmedString(rawSkuInfo.rentalCycle),
        skuCode: toTrimmedString(rawSkuInfo.skuCode),
        goodsSkuId: toTrimmedString(rawSkuInfo.goodsSkuId || rawSkuInfo.skuId)
      }
    : null;

  return {
    id: toTrimmedString(raw.id || goodsId),
    goodsId,
    name: toTrimmedString(raw.goodsName || raw.name),
    imgUrl: resolveAssetUrl(raw.imgUrl),
    targetType: toTrimmedString(raw.targetType),
    sort: toFiniteNumber(raw.sort),
    minPriceSkuInfo: skuInfo,
    price: skuInfo ? skuInfo.price : 0,
    skuId: skuInfo ? skuInfo.goodsSkuId : '',
    salesVolume,
    salesVolumeText: formatCompactNumber(salesVolume),
    score,
    scoreText: score > 0 ? score.toFixed(1) : '',
    tagText: toTrimmedString(raw.tagText),
    tagColor: toTrimmedString(raw.tagColor),
    rentalSchemes: normalizeRentalSchemes(raw.rentalSchemes)
  };
}

export function normalizeProducts(products = []) {
  return Array.isArray(products) ? products.map(normalizeProduct) : [];
}

/**
 * 商品卡专用印尼盾格式：整数不展示小数，非 0 小数展示。
 */
export function formatRupiah(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '';
  }

  return `Rp${formatRupiahAmount(Math.abs(number)) || '0'}`;
}

export const formatIntegerRupiah = formatRupiah;

export function getProductPriceText(product = {}, pendingText = '') {
  const normalized = normalizeProduct(product);
  return normalized.price > 0 ? formatRupiah(normalized.price) : pendingText;
}

export function getProductDetailQuery(product = {}) {
  const normalized = normalizeProduct(product);
  if (!normalized.goodsId) {
    return null;
  }

  const query = { goodsId: normalized.goodsId };
  if (normalized.skuId) {
    query.skuId = normalized.skuId;
  }
  return query;
}

export function filterProductsByKeyword(products = [], keyword = '') {
  if (!Array.isArray(products)) {
    return [];
  }

  const tokens = toTrimmedString(keyword)
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (tokens.length === 0) {
    return products.slice();
  }

  return products.filter(product => {
    const name = normalizeProduct(product).name.toLocaleLowerCase();
    return tokens.every(token => name.includes(token));
  });
}

export function updateSearchHistory(history = [], keyword = '', limit = 10) {
  const safeLimit = Math.max(0, Number(limit) || 0);
  const normalizedKeyword = toTrimmedString(keyword).replace(/\s+/g, ' ');
  const source = Array.isArray(history) ? history : [];
  const result = [];
  const seen = new Set();

  [normalizedKeyword, ...source].forEach(item => {
    const value = toTrimmedString(item).replace(/\s+/g, ' ');
    const identity = value.toLocaleLowerCase();
    if (!value || seen.has(identity) || result.length >= safeLimit) {
      return;
    }
    seen.add(identity);
    result.push(value);
  });

  return result;
}

export function getProductPaginationHasMore({
  total,
  loadedCount = 0,
  receivedCount = 0,
  pageSize = 20
} = {}) {
  const numericTotal = Number(total);
  if (Number.isFinite(numericTotal) && numericTotal > 0) {
    return Number(loadedCount) < numericTotal;
  }

  const safePageSize = Math.max(1, Number(pageSize) || 20);
  return Number(receivedCount) >= safePageSize;
}
